import { ElementRef } from '@angular/core';
import { Constants } from '../../models/constants';
import { GeoLocationService } from '../../services/geo-location.service';
import { GMapUtilities } from '../../utilities/gMap.utilities';
import { GoogleGeolocationResponseModel } from '../../viewModels/GoogleGeolocationResponseModel';
import { ZoneViewModel } from 'src/app/modules/settings/viewModels/zoneViewModel';
import { AreaViewModel } from 'src/app/modules/settings/viewModels/areaViewModel';
import { LocalisationService } from '../../services/localisation.service';
import { ObjectTypes } from '../../enums/objectTypes';
import { MapsLoaderService } from '../../services/mapsLoader.service';
import { Observable, Subscription, catchError, forkJoin, of } from 'rxjs';
import { IncidentsSettingsService } from '../../services/indicents-settings.service';
import { VenueMapsService } from 'src/app/modules/settings/services/venue-maps.service';
import { IVenueMapViewModel } from './venueMapViewModel.interface';
import { MapOverlayTypes } from '../../enums/maps/mapOverlayTypes.enum';
import { MapTileUrlValidator } from '../../utilities/maps/mapTileUrl.utilities';
import { ExternalService } from 'src/app/modules/external/services/external.service';
import { ExternalAuthService } from 'src/app/modules/external/services/external-auth.service';
import { MapTileFormats } from '../../enums/maps/mapTileFormats.enum';
import { TileUrlBuilder } from '../../utilities/maps/tileUrlBuilder.utilities';
import { createVenueMapGroundOverlay } from '../../models/map/overlays/venueMapGroundOverlay';
import { createVenueMapTilesOverlay } from '../../models/map/overlays/venueMapTilesOverlay';
import { IVenueMapOverlay } from '../../models/map/overlays/venueMapOverlay.interface';
import { CachingService } from '../../services/caching.service';
import { UrlService } from '../../services/url.service';
import { UploadTypes } from '../../enums/uploadTypes';

type drawableLoadedCallback = (zones: ZoneViewModel[], areas: AreaViewModel[], venueMaps: IVenueMapViewModel[]) => void;

export abstract class BaseMap {
  private mapContainer: ElementRef<HTMLElement>;

  private pressButtonTimer: NodeJS.Timeout = null;
  private tapAndHoldDetectionInitialized = false;
  private tipObj: HTMLDivElement = null;
  private tipOffset: { x: number; y: number } = {
    x: 20,
    y: 20,
  };

  // Used for getting the map projection and converting between geo point and xy point. ( Google Maps v3 api SUCKS!!!)
  private dummyOverlay: google.maps.OverlayView = null;

  private groundOverlaysBrightness = 0.75;

  protected static readonly DEFAULT_ZOOM = 15;
  protected static readonly MY_LOCATION_DEFAULT_ZOOM = 16;
  protected static readonly LOCATION_SEARCH_DEFAULT_ZOOM = 18;
  protected static readonly W3W_SERCH_DEFAULT_ZOOM = 19;

  protected map: google.maps.Map;
  protected defaultZoom = 15;

  // One to one mapping of the zones and areas ShapePointViewModel cached as google.maps.Polygon
  // so some operations can be applied on the map.
  protected zonePolygonMap: Map<number, google.maps.Polygon> = new Map();
  protected areaPolygonMap: Map<number, google.maps.Polygon> = new Map();
  protected myLocationLatLng: google.maps.LatLng;

  protected groundOverlays: IVenueMapOverlay[] = [];
  protected tileOverlays: IVenueMapOverlay[] = [];

  protected subscriptions: Subscription[] = [];

  //TODO: Later implement random ID generator prefix for extra security.
  private venueMapPrefix = 'venue-map-';

  public localisedZone = 'Zone';
  public localisedArea = 'Area';

  public venueMapsEnabled = false;

  constructor(
    protected readonly cachingService: CachingService,
    protected readonly mapsLoaderService: MapsLoaderService,
    protected readonly geoLocationService: GeoLocationService,
    protected readonly localisationService: LocalisationService,
    protected readonly venueMapsService: VenueMapsService,
    protected readonly incidentsSettingsService: IncidentsSettingsService,
    protected readonly urlService: UrlService,
    protected readonly externalService: ExternalService,
    protected readonly externalAuthService: ExternalAuthService) {
      const accountInfo = this.cachingService.GetAccountInfo();
      this.venueMapsEnabled = accountInfo ? accountInfo.useVenueMaps : false;
      this.localisedZone = this.localisationService.localiseObjectType(ObjectTypes.Zone);
      this.localisedArea = this.localisationService.localiseObjectType(ObjectTypes.Area);
    }

  get gmap(): google.maps.Map {
    return this.map;
  }

  get isMobile(): boolean {
    return window.innerWidth < Constants.xs;
  }

  get zonesPolygon(): google.maps.Polygon[] {
    return Array.from(this.zonePolygonMap.values());
  }

  get areasPolygon(): google.maps.Polygon[] {
    return Array.from(this.areaPolygonMap.values());
  }

  get polygonsAvailable(): boolean {
    return this.zonePolygonMap.size > 0;
  }

  protected get googleMapsAPIs(): Observable<any> {
    return this.mapsLoaderService.loadGoogleMapsAPIs();
  }

  protected initMapObservable(mapContainer: ElementRef<HTMLElement>, mapOptions: google.maps.MapOptions): Observable<void> {
    return new Observable<void>((observer) => {
        this.googleMapsAPIs.subscribe(() => {
          this.initMap(mapContainer, mapOptions);
          observer.next();
        })
      }
    );
  }

  protected initMapCallback(mapContainer: ElementRef<HTMLElement>, mapOptions: google.maps.MapOptions, callback: () => void): void {
    this.googleMapsAPIs.subscribe(() => {
      this.initMap(mapContainer, mapOptions);
      callback();
    });
  }

  protected initMap(mapContainer: ElementRef<HTMLElement>, mapOptions: google.maps.MapOptions): void {
    this.mapContainer = mapContainer;
    let mapParams: google.maps.MapOptions = {
      mapId: this.mapsLoaderService.mapConfiguration.map_id,
      zoom: 1,
      maxZoom: 22,
      center: new google.maps.LatLng(0, 0),
      controlSize: this.isMobile ? 32 : 26,
      mapTypeControlOptions: {
        mapTypeIds: ['roadmap', 'satellite', 'hybrid', 'terrain', 'focus'],
        position: google.maps.ControlPosition.BOTTOM_CENTER,
      },
      mapTypeId: google.maps.MapTypeId.ROADMAP,
      gestureHandling: 'cooperative',
      clickableIcons: false,
      cameraControl: true,
      cameraControlOptions: {
        position: google.maps.ControlPosition.LEFT_BOTTOM,
      },
      streetViewControl: true
    };

    if(mapOptions) {
      mapParams = {
        ...mapParams,
        ...mapOptions
      };
    }

    this.map = new google.maps.Map(mapContainer.nativeElement, mapParams);
    this.map.mapTypes.set('focus', GMapUtilities.getMapFocusStyle('Focus'));
    this.map.setMapTypeId('focus');
  }

  protected initMyLocation(callback: Function) {
    if (this.isMobile) {
      this.geoLocationService.getPosition().subscribe((position: any) => {
        const lat = position.coords.latitude as number;
        const lng = position.coords.longitude as number;
        this.myLocationLatLng = new google.maps.LatLng(lat, lng);
        callback(lat, lng);
      });
    } else {
      this.geoLocationService.getPositionFromGoogleGeolocationAPI().subscribe((response: GoogleGeolocationResponseModel) => {
        const lat = response.location.lat;
        const lng = response.location.lng;
        this.myLocationLatLng = new google.maps.LatLng(lat, lng);
        callback(lat, lng);
      });
    }
  }

  protected get isZonesPolygonCreated(): boolean {
    return this.zonePolygonMap.size > 0;
  }

  protected get isAreasPolygonCreated(): boolean {
    return this.areaPolygonMap.size > 0;
  }

  /**
   * As google maps javascript api doesn't naturally supports tap and hold event
   * this method takas care to implement it using sets of available events.
   *
   * @param durationMillis - The time in milliseconds which needs to pass in order
   * the event to be recognised as tap and hold event.
   *
   * @param callback callback function to execute when the event is recognised.
   */
  protected registerTapAndHoldEven(callback: Function, durationMillis = 800): void {
    if (!this.tapAndHoldDetectionInitialized) {
      if (durationMillis < 800) {
        durationMillis = 800;
      }

      this.map.addListener('mousedown', (event: google.maps.MapMouseEvent) => {
        this.pressButtonTimer = setTimeout(() => {
          if (callback && typeof callback === 'function') {
            callback(event);
          }
        }, durationMillis);
      });

      this.map.addListener('mouseup', () => {
        clearTimeout(this.pressButtonTimer);
      });

      this.map.addListener('dragstart', () => {
        clearTimeout(this.pressButtonTimer);
      });

      this.map.addListener('dragend', () => {
        clearTimeout(this.pressButtonTimer);
      });

      this.map.addListener('zoom_changed', () => {
        clearTimeout(this.pressButtonTimer);
      });
    }
  }

  /**
   * As google maps javascript api doesn't naturally supports tap and hold event
   * this method takas care to implement it using sets of available events.
   *
   * @param polygon the overlay to which the tam and hold regognition event to be
   * applied to.
   *
   * @param durationMillis - The time in milliseconds which needs to pass in order
   * the event to be recognised as tap and hold event.
   *
   * @param callback callback function to execute when the event is recognised.
   */
  protected registerTapAndHoldEvenOnPolygon(polygon: google.maps.Polygon, callback: Function, durationMillis = 800): void {
    if (!this.tapAndHoldDetectionInitialized) {
      if (durationMillis < 800) {
        durationMillis = 800;
      }

      polygon.addListener('mousedown', (event: google.maps.MapMouseEvent) => {
        this.pressButtonTimer = setTimeout(() => {
          if (callback && typeof callback === 'function') {
            callback(event);
          }
        }, 800);
      });

      polygon.addListener('mouseup', () => {
        clearTimeout(this.pressButtonTimer);
      });

      polygon.addListener('dragstart', () => {
        clearTimeout(this.pressButtonTimer);
      });

      polygon.addListener('dragend', () => {
        clearTimeout(this.pressButtonTimer);
      });

      polygon.addListener('zoom_changed', () => {
        clearTimeout(this.pressButtonTimer);
      });
    }
  }

  protected removeZonesPolygon(): void {
    this.zonePolygonMap.forEach(poly => {
      poly.setMap(null);
    });
    this.zonePolygonMap.clear();
  }

  protected removeAreasPolygon(): void {
    this.areaPolygonMap.forEach(poly => {
      poly.setMap(null);
    });
    this.areaPolygonMap.clear();
  }

  protected enableGroundOverlayBrightnessControl(): void {
    const observer = new MutationObserver(mutations => {
      for (const m of mutations) {
        m.addedNodes.forEach(node => {
          const element = node as HTMLImageElement;
          if (element.tagName === 'IMG' && element.parentElement?.style.opacity === '0.999') {
            element.parentElement.style.filter = `brightness(${this.groundOverlaysBrightness})`;
          }
        });
      }
    });
    observer.observe(this.mapContainer.nativeElement, { childList: true, subtree: true });
  }

  protected updateGroundOverlaysBrightness(value: number) {
    this.groundOverlaysBrightness = value;
    this.mapContainer.nativeElement.querySelectorAll('img').forEach(img => {
      if (img.parentElement?.style.opacity === '0.999') {
        img.parentElement.style.filter = `brightness(${value})`;
      }
    });
  }

  protected buildVenueMaps(venueMaps: IVenueMapViewModel[], showOnMap: boolean): void {
    const applicableVenueMaps = venueMaps?.filter(vm => vm.active) || [];

    this.buildGroundOverlays(applicableVenueMaps, showOnMap);
    this.buildTileOverlays(applicableVenueMaps, showOnMap);
  }

  private buildGroundOverlays(venueMaps: IVenueMapViewModel[], showOnMap: boolean): void {
    if (!venueMaps) {
      return;
    }

    venueMaps
      .filter(venueMap => venueMap.mapType === MapOverlayTypes.Ground_Overlay)
      .forEach(venueMap => {
        const bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(venueMap.southWestLatitude, venueMap.southWestLongitude),
          new google.maps.LatLng(venueMap.northEastLatitude, venueMap.northEastLongitude)
        );
        const url = venueMap.imageSource === UploadTypes.File
          ? this.urlService.buildResourceUrl(`/Resource/library/data/${venueMap.imageFileNameOrUrl}`)
          : venueMap.imageFileNameOrUrl;

        const VenueMapGroundOverlay = createVenueMapGroundOverlay();
        const groundOverlay = new VenueMapGroundOverlay(
          venueMap.id,
          url,
          bounds,
          {
            map: showOnMap ? this.map : null,
            clickable: false,
            opacity: 0.999 // Using 0.999 to allow custom styling via CSS filter
          }
        );
        groundOverlay.setLevel(venueMap.level);
        this.groundOverlays.push(groundOverlay);
      });
  }

  /**
   * Initializes tile overlays for venue maps by filtering, processing, and adding them to the Google Maps instance.
   *
   * This method processes venue maps with tile-based overlays by:
   * - Filtering maps to only include those with MapOverlayTypes.Tiles type
   * - Validating that each map has a valid tile URL and format
   * - Sorting maps by level in ascending order
   * - Creating Google Maps ImageMapType overlays with appropriate tile URL builders based on format
   * - Adding overlays to the map if toggle state is enabled
   *
   * Supported tile formats include XYZ, TMS, WMTS, and Quadkey schemes.
   * Overlays are always inserted at index 0 to maintain proper layering with dark overlays on top.
   *
   * @private
   * @returns {void}
   */
  private buildTileOverlays(venueMaps: IVenueMapViewModel[], showOnMap: boolean): void {
    if (!venueMaps) {
      return;
    }

    venueMaps
      .filter(venueMap => venueMap.mapType === MapOverlayTypes.Tiles)
      .filter(venueMap => {
        const tilesUrl = venueMap.mapTileURL?.trim();
        const mapTileFormat = venueMap.mapTileFormat || MapTileUrlValidator.detectScheme(tilesUrl);

        return !!tilesUrl && !!mapTileFormat
      })
      .sort((a, b) => a.level - b.level)
      .forEach(venueMap => {
        const tilesUrl = venueMap.mapTileURL.trim();
        const bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(venueMap.southWestLatitude, venueMap.southWestLongitude),
          new google.maps.LatLng(venueMap.northEastLatitude, venueMap.northEastLongitude)
        );

        const VenueMapTilesOverlay = createVenueMapTilesOverlay();
        const venueMapTilesOverlay = new VenueMapTilesOverlay(venueMap.id, {
          getTileUrl: (coord, zoom) => {
            if (this.tileWithinBounds(bounds, coord, zoom)) {
              let tilesFetchURL = '';
              const mapTyleFormat = venueMap.mapTileFormat || MapTileUrlValidator.detectScheme(tilesUrl);
              switch(mapTyleFormat) {
                case MapTileFormats.XYZ:
                  tilesFetchURL = TileUrlBuilder.xyz(tilesUrl, { x: coord.x, y: coord.y }, zoom);
                  break;
                case MapTileFormats.TMS:
                  tilesFetchURL = TileUrlBuilder.tms(tilesUrl, { x: coord.x, y: coord.y }, zoom);
                  break;
                case MapTileFormats.WMTS:
                  tilesFetchURL = TileUrlBuilder.wmts(tilesUrl, { x: coord.x, y: coord.y }, zoom);
                  break;
                case MapTileFormats.Quadkey:
                  tilesFetchURL = TileUrlBuilder.quadkey(tilesUrl, { x: coord.x, y: coord.y }, zoom);
                  break;
                default:
                  break;
              }

              return tilesFetchURL;
            }

            return null;
          },
          tileSize: new google.maps.Size(256, 256),
          name: this.getVenueMapId(venueMap),
          opacity: 1,
          maxZoom: 21
        });
        venueMapTilesOverlay.setLevel(venueMap.level);

        this.tileOverlays.push(venueMapTilesOverlay);
        if (showOnMap) {
          // !!! Always insert at index 0 to keep the dark overlay (if presented) on top !!!
          this.map.overlayMapTypes.insertAt(0, venueMapTilesOverlay);
        }
      });
  }


  protected buildPolygonsFromZonesAndAreas(zones: ZoneViewModel[], areas: AreaViewModel[], showOnMap: boolean): void {
    this.buildPolygonsFromZones(zones, showOnMap);
    this.buildPolygonsFromAreas(areas, showOnMap);
  }

  protected buildPolygonsFromZones(zones: ZoneViewModel[], showOnMap: boolean): void {
    if (zones?.length && this.zonePolygonMap.size === 0) {
      zones.forEach((zone) => {
        const zoneShapePointsArr = zone.zoneShapePoints;
        if (zoneShapePointsArr.length) {
          if(!this.zonePolygonMap.get(zone.id)) {
            const polygon = GMapUtilities.buildPolygonFromZoneModel(zoneShapePointsArr, showOnMap ? this.map : null);
            this.zonePolygonMap.set(zone.id, polygon);

            this.attachPolygonTooltipEvents(polygon, `${this.localisedZone}: ${zone.title}`, zone.levelIndex);
          }
        }
      });
    }
  }

  protected buildPolygonsFromAreas(areas: AreaViewModel[], showOnMap: boolean): void {
    if(areas?.length && this.areaPolygonMap.size === 0) {
      areas.forEach((area) => {
        const areaShapePointsArr = area.areaShapePoints;
        if (areaShapePointsArr.length) {
          if(!this.areaPolygonMap.get(area.id)) {
            const polygon = GMapUtilities.buildPolygonFromAreaModel(areaShapePointsArr, showOnMap ? this.map : null);
            this.areaPolygonMap.set(area.id, polygon);

            this.attachPolygonTooltipEvents(polygon, `${this.localisedArea}: ${area.title}`, area.levelIndex);
          }
        }
      });
    }
  }

  protected fitZonesBounds(): void {
    const latLngBounds = new google.maps.LatLngBounds();
    if (this.zonePolygonMap) {
      this.zonePolygonMap.forEach((zone) => {
        const zoneLatLngBounds = GMapUtilities.getPolygonLatLngBounds(zone);
        latLngBounds.union(zoneLatLngBounds);
      });
    }

    this.map.fitBounds(latLngBounds);
  }

  private attachPolygonTooltipEvents(polygon: google.maps.Polygon, tooltipText: string, indexLevel: number = 0): void {
    polygon.addListener('mouseover', (event: google.maps.PolyMouseEvent) => {
      this.injectTooltip(event, tooltipText, indexLevel);
    });

    polygon.addListener('mousemove', (event: google.maps.PolyMouseEvent) => {
      this.moveTooltip(event);
    });

    polygon.addListener('mouseout', () => {
      this.deleteTooltip();
    });
  }

  private injectTooltip(event: google.maps.PolyMouseEvent, text: string, indexLevel: number = 0): void {
    if (this.tipObj === null && event) {
      this.tipObj = document.createElement('div');
      this.tipObj.setAttribute('class', 'poly-tooltip');
      this.tipObj.innerHTML = `
        <div>${text}</div>
        <div>Level: ${indexLevel}</div>
      `;

      this.map.getDiv().appendChild(this.tipObj);

      if (this.dummyOverlay === null) {
        this.initDummyOverlay();
      }

      this.setTooltipAtLocation(event.latLng);
    }
  }

  private initDummyOverlay(): void {
    if (this.map !== null) {
      if (!this.dummyOverlay) {
        this.dummyOverlay = new google.maps.OverlayView();
        this.dummyOverlay.draw = () => void {};
        this.dummyOverlay.setMap(this.map);
      }
    }
  }

  private moveTooltip(event: google.maps.PolyMouseEvent): void {
    if (this.tipObj && event) {
      this.setTooltipAtLocation(event.latLng);
    }
  }

  private deleteTooltip(): void {
    if (this.tipObj !== null) {
      this.map.getDiv().removeChild(this.tipObj);
      this.tipObj = null;
    }
  }

  private setTooltipAtLocation(eventLatLng: google.maps.LatLng): void {
    if (this.dummyOverlay !== null && this.tipObj !== null && eventLatLng) {
      if (this.dummyOverlay.getProjection()) {
        const point = this.dummyOverlay.getProjection().fromLatLngToContainerPixel(eventLatLng);
        this.tipObj.style.top = point.y + window.scrollY + this.tipOffset.y + 'px';
        this.tipObj.style.left = point.x + window.scrollX + this.tipOffset.x + 'px';
      }
    }
  }

  /**
   * Determines if a map tile should be rendered based on its position relative to specified bounds.
   *
   * This method performs a multi-stage evaluation:
   * 1. Quick rejection if tile bounds don't intersect with the target bounds
   * 2. Immediate acceptance if tile is fully contained within bounds
   * 3. For partially intersecting tiles, applies a size constraint based on maxFactor
   *
   * @param bounds - The target geographical bounds to check against
   * @param coord - The tile coordinate (x, y) in the tile grid system
   * @param zoom - The zoom level for the tile
   * @param maxFactor - Maximum size multiplier for intersecting tiles (default: 1.3)
   *                   Controls how much larger a tile can be compared to bounds dimensions
   * @returns True if the tile should be rendered, false otherwise
   *
   * @remarks
   * For intersecting tiles, the method currently uses OR logic for width/height constraints.
   * This is marked as experimental and may be changed to AND logic in future versions.
   */
  protected tileWithinBounds(bounds: google.maps.LatLngBounds, coord: google.maps.Point, zoom: number, maxFactor: number = 1.3): boolean {
    const tileBounds = GMapUtilities.getTileLatLngBounds(coord, zoom);
    if (!tileBounds) {
      return false;
    }

    // Quick reject
    if (!GMapUtilities.boundsIntersects(bounds, tileBounds)) {
      return false;
    }

    // If fully inside, allow
    if (GMapUtilities.boundsFullyContains(bounds, tileBounds)) {
      return true;
    }

    // Intersecting only: apply size constraint
    const { width: tileW, height: tileH } = GMapUtilities.boundsSizeMeters(tileBounds);
    const { width: rectW, height: rectH } = GMapUtilities.boundsSizeMeters(bounds);

    const okWidth = tileW <= rectW * maxFactor;
    const okHeight = tileH <= rectH * maxFactor;

    //TODO: Experiment with AND vs OR here
    return okWidth || okHeight;
  }

  private getVenueMapId(venueMap: IVenueMapViewModel): string {
    return `${this.venueMapPrefix}${venueMap.id}`;
  }

  protected getZonesAndAreas(callback: drawableLoadedCallback) {
    const session = this.externalAuthService.getCurrentSession();
    const isExternal = !!session?.externalShareId;

    const zones$ = isExternal
      ? this.externalService.getAllZones(session.externalShareId)
      : this.incidentsSettingsService.getIncidentZonesViewModels();

    const areas$ = isExternal
      ? this.externalService.getAllAreas(session.externalShareId)
      : this.incidentsSettingsService.getAreas();

    this.subscriptions.push(
      forkJoin([
        zones$,
        areas$,
        this.venueMaps$()
      ]).subscribe(
        ([zones, areas, venueMaps]) => {
          callback(zones, areas, venueMaps);
        }
      )
    );
  }

  protected getVenueMaps(callback: (venueMaps: IVenueMapViewModel[]) => void) {
    this.subscriptions.push(
      this.venueMaps$().subscribe(callback)
    );
  }

  private venueMaps$(): Observable<IVenueMapViewModel[]> {
    if (!this.venueMapsEnabled) {
      return of<IVenueMapViewModel[]>([]);
    }

    return this.venueMapsService.list().pipe(
      catchError(err => {
        console.error('Failed to load venue maps', err);
        return of<IVenueMapViewModel[]>([]);
      })
    );
  }
}
